import React from 'react';
import './Icon.scss';

/**
 * Icon Component
 * Professional flat icons using Font Awesome
 */

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  className?: string;
}

const Icon: React.FC<IconProps> = ({ name, size = 24, color, className = '' }) => {
  // Mapping des noms d'icônes vers les classes Font Awesome
  const iconMapping: Record<string, string> = {
    // Admin Navigation
    'store': 'fa-solid fa-store',
    'users': 'fa-solid fa-users',
    'key': 'fa-solid fa-key',
    'shield': 'fa-solid fa-shield-halved',
    'tag': 'fa-solid fa-tag',
    'tags': 'fa-solid fa-tags',
    'settings': 'fa-solid fa-gear',
    'sliders': 'fa-solid fa-sliders',
    'clipboard': 'fa-solid fa-clipboard',
    'file-text': 'fa-solid fa-file-lines',
    'file-circle-plus': 'fa-solid fa-file-circle-plus',
    'plug': 'fa-solid fa-plug',
    'zap': 'fa-solid fa-bolt',
    'edit': 'fa-solid fa-pen-to-square',
    'edit-2': 'fa-solid fa-pen',
    
    // Orders & Actions
    'search': 'fa-solid fa-magnifying-glass',
    'filter': 'fa-solid fa-filter',
    'download': 'fa-solid fa-download',
    'upload': 'fa-solid fa-upload',
    'plus': 'fa-solid fa-plus',
    'minus': 'fa-solid fa-minus',
    'check': 'fa-solid fa-check',
    'check-double': 'fa-solid fa-check-double',
    'x': 'fa-solid fa-xmark',
    'trash': 'fa-solid fa-trash',
    'trash-2': 'fa-solid fa-trash-can',
    'eye': 'fa-solid fa-eye',
    'eye-off': 'fa-solid fa-eye-slash',
    
    // Navigation
    'chevron-left': 'fa-solid fa-chevron-left',
    'chevron-right': 'fa-solid fa-chevron-right',
    'chevron-up': 'fa-solid fa-chevron-up',
    'chevron-down': 'fa-solid fa-chevron-down',
    'chevrons-up': 'fa-solid fa-angles-up',
    'chevrons-down': 'fa-solid fa-angles-down',
    'angles-up': 'fa-solid fa-angles-up',
    'angles-down': 'fa-solid fa-angles-down',
    'menu': 'fa-solid fa-bars',
    'more-vertical': 'fa-solid fa-ellipsis-vertical',
    'more-horizontal': 'fa-solid fa-ellipsis',
    'user-search': 'fa-solid fa-magnifying-glass',
    
    // User & Profile
    'user': 'fa-solid fa-user',
    'user-plus': 'fa-solid fa-user-plus',
    'user-minus': 'fa-solid fa-user-minus',
    'user-check': 'fa-solid fa-user-check',
    'user-x': 'fa-solid fa-user-xmark',
    'log-out': 'fa-solid fa-right-from-bracket',
    'log-in': 'fa-solid fa-right-to-bracket',
    
    // Chat & Communication
    'message-square': 'fa-solid fa-comment',
    'message-circle': 'fa-solid fa-comment-dots',
    'compose': 'fa-solid fa-comment-medical',
    'dialpad': 'fa-solid fa-table-cells',
    'delete-left': 'fa-solid fa-delete-left',
    'send': 'fa-solid fa-paper-plane',
    'reply': 'fa-solid fa-reply',
    'mail': 'fa-solid fa-envelope',
    'phone': 'fa-solid fa-phone',
    'phone-slash': 'fa-solid fa-phone-slash',
    'phone-arrow-up': 'fa-solid fa-phone-arrow-up',
    'phone-arrow-down': 'fa-solid fa-phone-arrow-down',
    'whatsapp': 'fa-brands fa-whatsapp',
    'instagram': 'fa-brands fa-instagram',
    'facebook-messenger': 'fa-brands fa-facebook-messenger',
    'bell': 'fa-solid fa-bell',
    'smile': 'fa-regular fa-face-smile',
    'paw': 'fa-solid fa-paw',
    'utensils': 'fa-solid fa-utensils',
    'futbol': 'fa-solid fa-futbol',
    'car': 'fa-solid fa-car',
    'lightbulb': 'fa-solid fa-lightbulb',
    'flag': 'fa-solid fa-flag',
    'hashtag': 'fa-solid fa-hashtag',
    'paperclip': 'fa-solid fa-paperclip',
    'image': 'fa-regular fa-image',
    
    // Status & Feedback
    'alert-circle': 'fa-solid fa-circle-exclamation',
    'alert-triangle': 'fa-solid fa-triangle-exclamation',
    'info': 'fa-solid fa-circle-info',
    'help-circle': 'fa-solid fa-circle-question',
    'check-circle': 'fa-solid fa-circle-check',
    'x-circle': 'fa-solid fa-circle-xmark',
    'lock': 'fa-solid fa-lock',
    
    // Location & Tracking
    'map': 'fa-solid fa-map',
    'mapPin': 'fa-solid fa-location-dot',
    'map-pin': 'fa-solid fa-map-pin',
    'navigation': 'fa-solid fa-location-arrow',
    
    // Time & Calendar
    'calendar': 'fa-solid fa-calendar',
    'clock': 'fa-solid fa-clock',
    
    // Documents & Files
    'folder': 'fa-solid fa-folder',
    'file': 'fa-solid fa-file',
    'copy': 'fa-solid fa-copy',
    'save': 'fa-solid fa-floppy-disk',
    
    // Business
    'package': 'fa-solid fa-box',
    'archive': 'fa-solid fa-box-archive',
    'rotate-ccw': 'fa-solid fa-rotate-left',
    'qrcode': 'fa-solid fa-qrcode',
    'barcode': 'fa-solid fa-barcode',
    'truck': 'fa-solid fa-truck',
    'shopping-cart': 'fa-solid fa-cart-shopping',
    'sparkles': 'fa-solid fa-wand-magic-sparkles',
    'credit-card': 'fa-solid fa-credit-card',
    'dollar-sign': 'fa-solid fa-dollar-sign',
    
    // UI Elements
    'loader': 'fa-solid fa-spinner fa-spin',
    'refresh': 'fa-solid fa-rotate',
    'refresh-cw': 'fa-solid fa-rotate',
    'rotateCw': 'fa-solid fa-rotate',
    'arrows-rotate': 'fa-solid fa-arrows-rotate',
    'sync': 'fa-solid fa-arrows-rotate',
    'external-link': 'fa-solid fa-up-right-from-square',
    'expand': 'fa-solid fa-expand',
    'palette': 'fa-solid fa-palette',
    'pin': 'fa-solid fa-thumbtack',
    'pin-off': 'fa-solid fa-thumbtack',
    'globe': 'fa-solid fa-globe',
    'link': 'fa-solid fa-link',
    'star': 'fa-solid fa-star',
    'heart': 'fa-solid fa-heart',
    'home': 'fa-solid fa-house',
    'grid': 'fa-solid fa-grip',
    'grip-vertical': 'fa-solid fa-grip-vertical',
    'list': 'fa-solid fa-list',
    'table': 'fa-solid fa-table',
    'layers': 'fa-solid fa-layer-group',
    'object-group': 'fa-solid fa-object-group',
    'bar-chart': 'fa-solid fa-chart-column',
    'bar-chart-2': 'fa-solid fa-chart-column',
    'chart-bar': 'fa-solid fa-chart-column',
    'chart': 'fa-solid fa-chart-line',
    'pie-chart': 'fa-solid fa-chart-pie',
    'gauge': 'fa-solid fa-gauge-high',
    'trending-up': 'fa-solid fa-arrow-trend-up',
    'layout': 'fa-solid fa-table-columns',
    
    // Media & Playback
    'play': 'fa-solid fa-play',
    'pause': 'fa-solid fa-pause',
    'hourglass': 'fa-solid fa-hourglass-half',
    'stop': 'fa-solid fa-stop',
    'video': 'fa-solid fa-video',
    'file-pdf': 'fa-solid fa-file-pdf',
    'mic': 'fa-solid fa-microphone',
    'mic-off': 'fa-solid fa-microphone-slash',
    'volume': 'fa-solid fa-volume-low',
    'volume-high': 'fa-solid fa-volume-high',
    
    // History & Time
    'history': 'fa-solid fa-clock-rotate-left',
    
    // Close & Cancel
    'close': 'fa-solid fa-xmark',
    
    // Workflow & Automation
    'workflow': 'fa-solid fa-diagram-project',
    
    // Arrows
    'arrow-left': 'fa-solid fa-arrow-left',
    'arrow-right': 'fa-solid fa-arrow-right',
    'arrow-up': 'fa-solid fa-arrow-up',
    'arrow-down': 'fa-solid fa-arrow-down'
  };

  const faClass = iconMapping[name] || 'fa-solid fa-circle-question'; // Fallback icon

  const style: React.CSSProperties = {
    fontSize: `${size}px`,
    ...(color && { color })
  };

  return (
    <i 
      className={`${faClass} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

export default Icon;
